/**
 * Tennis API Sync Panel — admin-only UI on AdminPage → Tennis Results tab.
 *
 * Lets an admin pick a tennis league, a tournament (men's or women's draw),
 * and a date range, then press the "DO TENNIS API CALL" button. Calling
 * `triggerTennisSync` POSTs to /api/tennis-sync, logs the HTTP status to the
 * browser console (visible in DevTools per the user's explicit requirement),
 * and on a 200 triggers a leaderboard snapshot for the league.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { League, TennisTournament } from '../../types/database';
import { tennisTournamentService } from '../../services/firestore';
import { triggerTennisSync } from '../../services/tennisSync';

type Props = {
  leagues: League[];
};

type LastRun = {
  status: number;
  ok: boolean;
  message: string;
  body: unknown;
  at: Date;
};

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const TennisApiSyncPanel: React.FC<Props> = ({ leagues }) => {
  const tennisLeagues = useMemo(
    () => leagues.filter((l) => l.sport === 'tennis'),
    [leagues]
  );

  const [tournaments, setTournaments] = useState<TennisTournament[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedTournamentKey, setSelectedTournamentKey] = useState<number | ''>('');
  const [dateStart, setDateStart] = useState<string>(todayIso());
  const [dateStop, setDateStop] = useState<string>(todayIso());
  const [loading, setLoading] = useState(false);
  const [lastRun, setLastRun] = useState<LastRun | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const all = await tennisTournamentService.listAll();
        // Stable order: name asc, then men before women
        all.sort((a, b) => {
          const n = (a.tournamentName || '').localeCompare(b.tournamentName || '');
          if (n !== 0) return n;
          return (a.eventType || '').localeCompare(b.eventType || '');
        });
        setTournaments(all);
      } catch (err) {
        // Tournaments collection may be empty until the seed script runs.
        // eslint-disable-next-line no-console
        console.warn('Failed to load tennis tournaments — run scripts/seed-tennis-tournaments.mjs?', err);
      }
    })();
  }, []);

  // When league changes, default the tournament dropdown to the league's
  // configured men's key (if any).
  useEffect(() => {
    if (!selectedLeagueId) return;
    const league = tennisLeagues.find((l) => l.id === selectedLeagueId);
    if (league?.tennisMensTournamentKey) {
      setSelectedTournamentKey(league.tennisMensTournamentKey);
    }
  }, [selectedLeagueId, tennisLeagues]);

  const canSubmit =
    !loading &&
    selectedLeagueId &&
    typeof selectedTournamentKey === 'number' &&
    dateStart &&
    dateStop;

  async function handleSync() {
    if (!canSubmit) return;
    setLoading(true);
    try {
      const result = await triggerTennisSync(
        selectedLeagueId,
        {
          tournamentKey: selectedTournamentKey as number,
          dateStart,
          dateStop,
        }
      );
      const body = result.body as { fixtureCount?: number; matchCount?: number; error?: string };
      const message = result.ok
        ? `Synced ${body.matchCount ?? 0} match(es) (${body.fixtureCount ?? 0} from API).`
        : `Sync failed: ${body.error || 'unknown error'} (HTTP ${result.status})`;
      setLastRun({
        status: result.status,
        ok: result.ok,
        message,
        body: result.body,
        at: new Date(),
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setLastRun({
        status: 0,
        ok: false,
        message: `Network error: ${msg}`,
        body: null,
        at: new Date(),
      });
    } finally {
      setLoading(false);
    }
  }

  const selectedLeague = tennisLeagues.find((l) => l.id === selectedLeagueId) || null;
  const mensKey = selectedLeague?.tennisMensTournamentKey;
  const womensKey = selectedLeague?.tennisWomensTournamentKey;

  return (
    <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Pull live match data from api-tennis.com
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fetches fixtures, scores, and statistics for the selected tournament and date range.
        Each press creates a sync run record and refreshes the league&apos;s leaderboard snapshot.
        Re-presses with the same range safely update existing matches in place (postponement-safe).
      </Typography>

      <Stack spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel id="tennis-sync-league-label">Tennis league</InputLabel>
          <Select
            labelId="tennis-sync-league-label"
            value={selectedLeagueId}
            label="Tennis league"
            onChange={(e) => setSelectedLeagueId(e.target.value as string)}
          >
            {tennisLeagues.length === 0 && (
              <MenuItem value="" disabled>
                No tennis leagues found
              </MenuItem>
            )}
            {tennisLeagues.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
                {l.tournamentName ? `  (${l.tournamentName})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id="tennis-sync-tournament-label">Tournament (draw)</InputLabel>
          <Select
            labelId="tennis-sync-tournament-label"
            value={selectedTournamentKey === '' ? '' : String(selectedTournamentKey)}
            label="Tournament (draw)"
            onChange={(e) => {
              const v = e.target.value;
              setSelectedTournamentKey(v === '' ? '' : Number(v));
            }}
          >
            {tournaments.length === 0 && (
              <MenuItem value="" disabled>
                No tournaments seeded yet — run scripts/seed-tennis-tournaments.mjs
              </MenuItem>
            )}
            {tournaments.map((t) => {
              const isMens = mensKey === t.tournamentKey;
              const isWomens = womensKey === t.tournamentKey;
              const label = `${t.tournamentName} — ${t.eventType}` +
                (t.surface ? ` (${t.surface})` : '') +
                (isMens ? '  ← league men\'s draw' : '') +
                (isWomens ? '  ← league women\'s draw' : '');
              return (
                <MenuItem key={t.tournamentKey} value={String(t.tournamentKey)}>
                  {label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Date start"
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            label="Date stop"
            type="date"
            value={dateStop}
            onChange={(e) => setDateStop(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
        </Box>

        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSync}
            disabled={!canSubmit}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ fontWeight: 'bold', letterSpacing: 1 }}
          >
            {loading ? 'SYNCING…' : 'DO TENNIS API CALL'}
          </Button>
        </Box>

        {lastRun && (
          <Alert severity={lastRun.ok ? 'success' : 'error'}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {lastRun.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              HTTP {lastRun.status} · {lastRun.at.toLocaleTimeString()}
            </Typography>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default TennisApiSyncPanel;
